import React, { useMemo, useState } from "react";
import { ChartBar } from "@phosphor-icons/react";

// Salary spine of the portfolio: where the money sits before you export.
// Bands mirror the engine's punt-spine tiers so a red cell means a real
// rule was stretched, not a vibe.
const BANDS = [
  { key: "u1k", label: "< $1k", min: 0, max: 999, capKey: "punt_cap_under_1k_pct", punt: true },
  { key: "u3k", label: "$1k–$2.9k", min: 1000, max: 2999, capKey: "punt_cap_under_3k_pct", punt: true },
  { key: "u5k", label: "$3k–$4.9k", min: 3000, max: 4999, capKey: "punt_cap_under_5k_pct", punt: false },
  { key: "u7k", label: "$5k–$6.9k", min: 5000, max: 6999, capKey: null, punt: false },
  { key: "stud", label: "$7k+", min: 7000, max: Infinity, capKey: null, punt: false },
];

const baseSalary = (p) => Number(p?.salary || 0);

export default function SpineHeatmap({ lineups, showdownRules, config }) {
  const [open, setOpen] = useState(true);
  const portfolioCap = Number(
    showdownRules?.max_exposure_pct ?? config?.max_exposure_pct ?? 0,
  ) || null;
  const maxCheapBodies = showdownRules?.max_cheap_bodies ?? null;

  const stats = useMemo(() => {
    const n = (lineups || []).length;
    if (!n) return null;
    const rows = BANDS.map((b) => ({ ...b, lineups: 0, bodies: 0, byPlayer: {} }));
    let cheapViolations = 0;
    (lineups || []).forEach((L) => {
      const players = L?.players || [];
      const hit = new Set();
      let cheap = 0;
      players.forEach((p) => {
        const sal = baseSalary(p);
        const band = rows.find((b) => sal >= b.min && sal <= b.max);
        if (!band) return;
        band.bodies += 1;
        hit.add(band.key);
        if (band.key === "u1k") cheap += 1;
        const k = p.name || p.dk_id;
        band.byPlayer[k] = (band.byPlayer[k] || 0) + 1;
      });
      if (maxCheapBodies != null && cheap > maxCheapBodies) cheapViolations += 1;
      rows.forEach((b) => { if (hit.has(b.key)) b.lineups += 1; });
    });
    return {
      n,
      cheapViolations,
      rows: rows.map((b) => {
        const top = Object.entries(b.byPlayer).sort((x, y) => y[1] - x[1])[0] || null;
        const capPct = b.capKey && showdownRules?.[b.capKey] != null
          ? Number(showdownRules[b.capKey])
          : portfolioCap;
        const topPct = top ? (100 * top[1]) / n : 0;
        return {
          ...b,
          exposurePct: (100 * b.lineups) / n,
          perLineup: b.bodies / n,
          topName: top ? top[0] : null,
          topPct,
          capPct,
          breach: capPct != null && topPct > capPct + 0.51,
        };
      }),
    };
  }, [lineups, showdownRules, portfolioCap, maxCheapBodies]);

  if (!stats) return null;
  const maxExposure = Math.max(...stats.rows.map((r) => r.exposurePct), 1);
  const breaches = stats.rows.filter((r) => r.breach).length + (stats.cheapViolations > 0 ? 1 : 0);

  return (
    <section className="bg-[#0a0a0a] border border-zinc-800" data-testid="spine-heatmap">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="spine-heatmap-toggle"
        className="w-full px-4 py-2.5 flex items-center justify-between border-b border-zinc-800 hover:bg-zinc-900/40 transition-colors"
      >
        <span className="overline inline-flex items-center gap-2">
          <ChartBar size={13} weight="fill" className="text-[#39FF14]" /> Spine Heatmap · salary tiers
        </span>
        <span className="mono text-[10px] uppercase tracking-widest">
          {breaches > 0
            ? <span className="text-[#FF3B30] font-bold" data-testid="spine-heatmap-breaches">{breaches} tier{breaches === 1 ? "" : "s"} over cap</span>
            : <span className="text-[#39FF14]" data-testid="spine-heatmap-clean">spine clean</span>}
          <span className="text-zinc-600"> · {stats.n} lineups {open ? "▾" : "▸"}</span>
        </span>
      </button>
      {open && (
        <div className="p-3">
          <div className="grid grid-cols-5 gap-2">
            {stats.rows.map((r) => (
              <div
                key={r.key}
                data-testid={`spine-band-${r.key}`}
                title={`${r.label} · ${r.exposurePct.toFixed(0)}% of lineups use this tier · ${r.perLineup.toFixed(2)} bodies per lineup${r.topName ? ` · most-used: ${r.topName} ${r.topPct.toFixed(0)}%` : ""}${r.capPct != null ? ` · tier cap ${r.capPct}%` : ""}`}
                className={`relative overflow-hidden border px-2 py-2 ${
                  r.breach ? "border-[#FF3B30] bg-[#FF3B30]/10" : r.punt ? "border-[#FFCC00]/40" : "border-zinc-800"
                }`}
              >
                <div
                  className={`absolute inset-x-0 bottom-0 ${r.breach ? "bg-[#FF3B30]/25" : "bg-[#39FF14]/12"}`}
                  style={{ height: `${Math.round((r.exposurePct / maxExposure) * 100)}%` }}
                />
                <div className="relative">
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-400 flex items-center gap-1">
                    {r.label}{r.punt && <span className="text-[#FFCC00]">punt</span>}
                  </div>
                  <div className={`mono text-lg font-black leading-tight ${r.breach ? "text-[#FF3B30]" : "text-white"}`}
                    data-testid={`spine-band-pct-${r.key}`}>
                    {r.exposurePct.toFixed(0)}%
                  </div>
                  <div className="mono text-[9px] text-zinc-500">{r.perLineup.toFixed(2)} / lineup</div>
                  {r.topName && (
                    <div className={`mono text-[9px] truncate ${r.breach ? "text-[#FF3B30]" : "text-zinc-600"}`}>
                      {r.topName} {r.topPct.toFixed(0)}%{r.capPct != null ? ` / ${r.capPct}%` : ""}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mono text-[9px] text-zinc-500 mt-2 leading-relaxed">
            Bar height = share of lineups that use the tier. The player line is the most-used body in that tier against its
            exposure cap — red means that cap was stretched.
            {stats.cheapViolations > 0 && (
              <span className="text-[#FF3B30] font-bold" data-testid="spine-cheap-violations">
                {" "}· {stats.cheapViolations} lineup{stats.cheapViolations === 1 ? "" : "s"} carry more than {maxCheapBodies} sub-$1k body.
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
